import React from 'react';
import {useSelector, useStore} from 'react-redux';
import {useLocation} from 'react-router';
import {history} from '~/CreateFabricGreetingCard/history';
import {createSteps} from './createSteps';
import {getLastPathComponent, backToGallery} from './utils';
import {saveEventThunk} from '~/CreateFabricGreetingCard/api/event';
import {loginModalPromise} from '~/CreateFabricGreetingCard/utils/modalPromises';
import {
  webViewOnNav,
  webViewSetBusy,
  webViewSetEventId,
} from '~/CreateFabricGreetingCard/webview/webview';
import {isFabricWebview} from '~/common/utils/webview';
import {trackEventId} from '~/CreateFabricGreetingCard/utils/dataTracking';
import {eventInEditMode} from '~/CreateFabricGreetingCard/reducers/details/selectors';
import {toastAlert} from '~library/atoms/Toast';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

/**
 * Check the CreateFabricInvitation/navigation/README.md file for info on the steps
 */

export const getCurrentStep = () => {
  const {location} = window;
  const lastPathComponent = getLastPathComponent(location.pathname);
  return createSteps[lastPathComponent];
};

export const useCurrentRoute = () => {
  const inEditMode = useSelector(eventInEditMode);
  const location = useLocation(); // get updated when the path changes
  const currentStep = getCurrentStep(inEditMode);
  const store = useStore();

  currentStep.nextRoute =
    typeof currentStep.nextRoute === 'function'
      ? currentStep.nextRoute(store)
      : currentStep.nextRoute;
  currentStep.nextButtonText =
    typeof currentStep.nextButtonText === 'function'
      ? currentStep.nextButtonText(store)
      : currentStep.nextButtonText;
  const nextStep = createSteps[currentStep.nextRoute];
  const nextStepAvailable = useSelector(currentStep.nextRouteAvailableSelector || (() => true));
  const nextStepPreEnterRouteStatusMessage = nextStep?.preEnterRouteStatusMessage;

  const previousStep = currentStep.previousRoute;
  const lastPathComponent = getLastPathComponent(location.pathname);

  return {
    ...currentStep,
    path: currentStep.path || lastPathComponent,
    meta: createSteps.meta,
    nextRouteAvailableSelector: null,
    nextStepAvailable,
    nextPath: nextStep?.path,
    previousPath: previousStep,
    nextStepPreEnterRouteStatusMessage,
    nextButtonComponent: currentStep.nextButtonComponent
      ? currentStep.nextButtonComponent(store)
      : null,
  };
};

export const navigateToRoute = async (routeNameArg, store, force, searchParams) => {
  let routeName = routeNameArg;
  if (!routeName) {
    return backToGallery();
  }

  const {inEditMode} = store.getState().details;
  const checkedOut = store.getState().review?.checkedOut;
  const currentStep = getCurrentStep(inEditMode);

  if (routeName === 'next') {
    if (typeof currentStep.nextRoute === 'function') {
      routeName = currentStep.nextRoute(store);
    } else {
      routeName = currentStep.nextRoute;
    }
  }
  if (routeName === 'back') window.history.back();
  if (!force && currentStep.preLeaveRouteFunction) {
    try {
      const cont = await currentStep.preLeaveRouteFunction(store);
      if (cont === false)
        /* preLeaveRouteFunction returns false to abort navigation */
        return undefined;
    } catch (e) {
      if (e && e.message) {
        evite.error('Failure in preLeaveRouteFunction for greeting cards', e);
        toastAlert(e.message, {
          icon: <AlertLineIcon />,
          type: 'error',
          position: 'top-right',
        });
      }
      return undefined;
    }
  }
  if (routeName === 'previous') {
    if (typeof currentStep.previousRoute === 'function') {
      routeName = currentStep.previousRoute(store);
    } else {
      routeName = currentStep.previousRoute;
    }
  }
  if (!force && currentStep.saveOnLeave && !checkedOut) {
    webViewSetBusy(true);
    try {
      const userId = store.getState().user?.userId;
      if (!userId)
        try {
          await loginModalPromise(store.dispatch);
        } catch (e) {
          // login window canceled
          return undefined;
        }
      const origEventId = store.getState().eviteEvent.id;
      await store.dispatch(saveEventThunk(true));
      const newEventId = store.getState().eviteEvent.id;
      if (newEventId !== origEventId) {
        webViewSetEventId(newEventId);
        trackEventId(newEventId);
      }
    } catch (e) {
      webViewSetBusy(false);
      return false;
    } finally {
      webViewSetBusy(false);
    }
  }
  if (typeof routeName === 'function') {
    routeName = routeName(store);
  }

  const route = createSteps[routeName];
  if (!route) {
    return undefined;
  }
  if (!force && route.preEnterRouteFunction) {
    try {
      webViewSetBusy(true);
      const cont = await route.preEnterRouteFunction(store);
      if (cont === false)
        /* preEnterRouteFunction returns false to abort navigation */
        return undefined;
    } catch (e) {
      if (e && e.message) {
        evite.error('Failure in preEnterRouteFunction for greeting cards', e);
        toastAlert(e.message, {
          icon: <AlertLineIcon />,
          type: 'error',
          position: 'top-right',
        });
      }
      return undefined;
    } finally {
      webViewSetBusy(false);
    }
  }
  if (isFabricWebview() && route.hideInWebView) {
    // Only notify the app of the new route, don't update our pathname
    const nextButtonText =
      typeof route.nextButtonText === 'function'
        ? route.nextButtonText(store)
        : route.nextButtonText;
    webViewOnNav(routeName, route.nextRoute, route.previousRoute, nextButtonText);
  } else if (route.path.indexOf('/') < 0) {
    const components = history.location.pathname.split('/');
    components[components.length - 1] = route.path;
    let {search} = history.location;
    if (searchParams) {
      search = `${search}&${new URLSearchParams(searchParams).toString()}`;
    }

    history.push({
      pathname: components.join('/'),
      hash: history.location.hash,
      search,
    });
  } else if (routeName === 'exit' && currentStep.id === 1) {
    window.history.back();
  } else {
    window.location = route.path;
  }
  return undefined;
};

export const setupWebviewNavigation = (store) => {
  window.webview = window.webview || {};
  window.webview.navigateToRoute = async (route) => navigateToRoute(route, store);
};
